export const ZELLE_QR_IMAGE_URL = "https://krumago-application-assets.s3.us-east-1.amazonaws.com/Flex+Office+Domain+Assets/Zelle+QR.jpg";
export const OFFLINE_PAYMENT_PENDING_STATUS = "PAYMENT_PENDING";
export const OFFLINE_PAYMENT_PROCESSING_STATUS = "PAYMENT_PROCESSING";

export const resolveInvoicePaymentCompany = (invoice, order, storedCompanyDetails) => (
  invoice?.company || order?.company || storedCompanyDetails || null
);

export const resolveInvoiceCustomerAccountId = (invoice, order, paymentUser) => (
  order?.account?.primaryId
  || order?.accountId
  || invoice?.order?.account?.primaryId
  || invoice?.order?.accountId
  || paymentUser?.account?.primaryId
  || null
);

const normalizeCountryCode = (countryCode = "") => String(countryCode).toUpperCase();
const normalizeValue = (value = "") => String(value || "").trim().toLowerCase();
const normalizeMethodType = (value = "") => String(value || "").trim().toUpperCase();
const sanitizeOptionValue = (value = "") => (
  normalizeValue(value)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
);

const getGatewayOptionValue = (countryCode, gatewayMethod = null) => {
  const normalizedGatewayName = normalizeValue(gatewayMethod?.name);

  if (normalizedGatewayName.includes("stripe")) return "stripe";
  if (normalizedGatewayName.includes("razor")) return "razorPay";

  return normalizeCountryCode(countryCode) === "US" ? "stripe" : "razorPay";
};

const getGatewayOptionTitle = (countryCode, gatewayMethod = null) => {
  const normalizedGatewayName = normalizeValue(gatewayMethod?.name);

  if (normalizedGatewayName.includes("stripe")) return "Card Payment";
  if (normalizedGatewayName.includes("razor")) return "Razorpay";

  return normalizeCountryCode(countryCode) === "US" ? "Card Payment" : "Razorpay";
};

const getGatewayOptionDescription = (countryCode, gatewayMethod = null) => {
  const normalizedGatewayName = normalizeValue(gatewayMethod?.name);

  if (normalizedGatewayName.includes("stripe")) return "Pay securely online using your card.";
  if (normalizedGatewayName.includes("razor")) return "Continue with secure online checkout.";

  return normalizeCountryCode(countryCode) === "US"
    ? "Pay securely online using your card."
    : "Continue with secure online checkout.";
};

const isGatewayMethod = (method) => normalizeMethodType(method?.paymentMethodType) === "PAYMENT_GATEWAY";
const isCashMethod = (method) => normalizeMethodType(method?.paymentMethodType) === "CASH";
const isBankAccountMethod = (method) => normalizeMethodType(method?.paymentMethodType) === "BANK_ACCOUNT";
const isPaymentAppMethod = (method) => normalizeMethodType(method?.paymentMethodType) === "PAYMENT_APP";
const isEducationDomain = (companyDetails = null) => {
  const storedDomainType = typeof window !== "undefined"
    ? window.localStorage?.getItem("domainType")
    : "";
  const domainCandidates = [
    companyDetails?.domain?.type,
    companyDetails?.domainType,
    companyDetails?.domain,
    storedDomainType
  ];

  return domainCandidates.some((domainType) => normalizeMethodType(domainType) === "EDUCATION");
};
const shouldHideCashPaymentOption = (companyDetails = null) => isEducationDomain(companyDetails);
const filterPaymentOptionsByDomain = (options = [], companyDetails = null) => (
  shouldHideCashPaymentOption(companyDetails)
    ? options.filter((option) => option?.category !== "offline_cash")
    : options
);

const isZelleMethod = (method) => normalizeValue(method?.name).includes("zelle");
const isBankTransferMethod = (method) => isBankAccountMethod(method) && normalizeValue(method?.name).includes("bank transfer");
const isUpiMethod = (method) => normalizeValue(method?.name) === "upi";
const isQrCodeMethod = (method) => {
  const normalizedName = normalizeValue(method?.name);
  return normalizedName === "qr code" || normalizedName === "qr_code";
};

const getConfiguredPaymentMethods = (paymentMethods = []) => (
  Array.isArray(paymentMethods) ? paymentMethods.filter(Boolean) : []
);

const getLegacyFallbackOptions = (countryCode) => {
  const gatewayLabel = getGatewayDisplayLabel(countryCode);
  return [
    {
      value: getDefaultGatewayPaymentMethod(countryCode),
      title: gatewayLabel,
      label: gatewayLabel,
      description: getGatewayOptionDescription(countryCode),
      orderPaymentMethod: "PAYMENT_GATEWAY",
      paymentMethodType: "PAYMENT_GATEWAY",
      category: "gateway",
      isOffline: false,
      requiresReference: false,
      hasQrCode: false,
      paymentMethodConfig: null
    },
    {
      value: "cash",
      title: "Cash",
      label: "Cash",
      description: "Reserve now and pay in person at the venue.",
      orderPaymentMethod: "CASH",
      paymentMethodType: "CASH",
      category: "offline_cash",
      isOffline: true,
      requiresReference: false,
      hasQrCode: false,
      paymentMethodConfig: null
    },
    {
      value: "zelle",
      title: "Zelle",
      label: "Zelle",
      description: "Use the QR code and mark the order for manual verification.",
      orderPaymentMethod: "ZELLE",
      paymentMethodType: "BANK_ACCOUNT",
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: true,
      paymentMethodConfig: null
    }
  ];
};

export const getGatewayPaymentMethod = (paymentMethods = []) => (
  getConfiguredPaymentMethods(paymentMethods).find(isGatewayMethod) || null
);

export const getDefaultGatewayPaymentMethod = (countryCode, paymentMethods = []) => (
  getGatewayOptionValue(countryCode, getGatewayPaymentMethod(paymentMethods))
);

export const getGatewayProvider = (paymentMethod, countryCode, paymentMethods = []) => {
  if (paymentMethod === "stripe") return "Stripe";
  if (paymentMethod === "razorPay") return "Razorpay";

  return getGatewayOptionTitle(countryCode, getGatewayPaymentMethod(paymentMethods));
};

export const getCurrencyForCountry = (countryCode) => (
  normalizeCountryCode(countryCode) === "US" ? "USD" : "INR"
);

export const getGatewayDisplayLabel = (countryCode, paymentMethods = []) => (
  getGatewayOptionTitle(countryCode, getGatewayPaymentMethod(paymentMethods))
);

export const getGatewayCredentials = (paymentMethods = []) => {
  const gatewayMethod = getGatewayPaymentMethod(paymentMethods);
  return {
    keyID: gatewayMethod?.keyID || "",
    keySecret: gatewayMethod?.keySecret || "",
    webhookSecret: gatewayMethod?.webhookSecret || ""
  };
};

export const getPaymentMethodQrImageUrl = (paymentMethod = null) => {
  const qrImageUrl = paymentMethod?.appQRCodeImage?.displayLink
    || paymentMethod?.appQRCodeImage?.storedLink
    || "";

  if (qrImageUrl) {
    return qrImageUrl;
  }

  return isZelleMethod(paymentMethod) ? ZELLE_QR_IMAGE_URL : "";
};

const isManualReferenceMethod = (method) => (
  isBankTransferMethod(method)
  || isUpiMethod(method)
  || isQrCodeMethod(method)
  || isZelleMethod(method)
  || isPaymentAppMethod(method)
  || isBankAccountMethod(method)
);

const buildPaymentOptionFromMethod = (method, countryCode) => {
  if (isGatewayMethod(method)) {
    const gatewayTitle = getGatewayOptionTitle(countryCode, method);
    return {
      value: getGatewayOptionValue(countryCode, method),
      title: gatewayTitle,
      label: gatewayTitle,
      description: getGatewayOptionDescription(countryCode, method),
      orderPaymentMethod: "PAYMENT_GATEWAY",
      paymentMethodType: "PAYMENT_GATEWAY",
      category: "gateway",
      isOffline: false,
      requiresReference: false,
      hasQrCode: false,
      paymentMethodConfig: method
    };
  }

  if (isCashMethod(method)) {
    const cashTitle = method?.name || "Cash";
    return {
      value: "cash",
      title: cashTitle,
      label: cashTitle,
      description: "Reserve now and pay in person at the venue.",
      orderPaymentMethod: "CASH",
      paymentMethodType: "CASH",
      category: "offline_cash",
      isOffline: true,
      requiresReference: false,
      hasQrCode: false,
      paymentMethodConfig: method
    };
  }

  if (isBankTransferMethod(method)) {
    const bankTransferTitle = method?.name || "Bank Transfer";
    return {
      value: "bank_transfer",
      title: bankTransferTitle,
      label: bankTransferTitle,
      description: "Use the saved bank account details and keep the transfer reference for verification.",
      orderPaymentMethod: "BANK_TRANSFER",
      paymentMethodType: "BANK_ACCOUNT",
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: false,
      paymentMethodConfig: method
    };
  }

  if (isUpiMethod(method)) {
    const upiTitle = method?.name || "UPI";
    return {
      value: "upi",
      title: upiTitle,
      label: upiTitle,
      description: "Use the saved UPI details and share your reference after payment.",
      orderPaymentMethod: "UPI",
      paymentMethodType: "PAYMENT_APP",
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: Boolean(getPaymentMethodQrImageUrl(method)),
      paymentMethodConfig: method
    };
  }

  if (isQrCodeMethod(method)) {
    const qrTitle = method?.name || "QR Code";
    return {
      value: "qr_code",
      title: qrTitle,
      label: qrTitle,
      description: "Scan the configured QR code and keep the confirmation for manual verification.",
      orderPaymentMethod: "QR_CODE",
      paymentMethodType: "PAYMENT_APP",
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: Boolean(getPaymentMethodQrImageUrl(method)),
      paymentMethodConfig: method
    };
  }

  if (isZelleMethod(method)) {
    const zelleTitle = method?.name || "Zelle";
    return {
      value: "zelle",
      title: zelleTitle,
      label: zelleTitle,
      description: "Use the saved Zelle details and submit the payment reference for verification.",
      orderPaymentMethod: "ZELLE",
      paymentMethodType: normalizeMethodType(method?.paymentMethodType) || "BANK_ACCOUNT",
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: Boolean(getPaymentMethodQrImageUrl(method)),
      paymentMethodConfig: method
    };
  }

  if (isPaymentAppMethod(method)) {
    const optionTitle = method?.name || "Payment App";
    return {
      value: sanitizeOptionValue(method?.name || "payment_app") || "payment_app",
      title: optionTitle,
      label: optionTitle,
      description: "",
      orderPaymentMethod: "PAYMENT_APP",
      paymentMethodType: normalizeMethodType(method?.paymentMethodType),
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: Boolean(getPaymentMethodQrImageUrl(method)),
      paymentMethodConfig: method
    };
  }

  if (isBankAccountMethod(method)) {
    const optionTitle = method?.name || "Bank Payment";
    return {
      value: sanitizeOptionValue(optionTitle) || "bank_account",
      title: optionTitle,
      label: optionTitle,
      description: "Use the saved payment details and submit your confirmation for manual verification.",
      orderPaymentMethod: "BANK_ACCOUNT",
      paymentMethodType: normalizeMethodType(method?.paymentMethodType),
      category: "offline_manual",
      isOffline: true,
      requiresReference: true,
      hasQrCode: Boolean(getPaymentMethodQrImageUrl(method)),
      paymentMethodConfig: method
    };
  }

  return null;
};

export const getSelectablePaymentOptions = (countryCode, paymentMethods = [], companyDetails = null) => {
  const configuredMethods = getConfiguredPaymentMethods(paymentMethods);

  if (configuredMethods.length === 0) {
    return filterPaymentOptionsByDomain(getLegacyFallbackOptions(countryCode), companyDetails);
  }

  const optionWeights = {
    gateway: 0,
    offline_cash: 1,
    offline_manual: 2
  };

  const usedValues = new Set();

  const options = configuredMethods
    .map((method) => buildPaymentOptionFromMethod(method, countryCode, companyDetails))
    .filter(Boolean)
    .map((option) => {
      let nextValue = option.value;
      let counter = 2;

      while (usedValues.has(nextValue)) {
        nextValue = `${option.value}_${counter}`;
        counter += 1;
      }

      usedValues.add(nextValue);
      return {
        ...option,
        value: nextValue
      };
    })
    .sort((left, right) => {
      const leftWeight = optionWeights[left.category] ?? 99;
      const rightWeight = optionWeights[right.category] ?? 99;

      if (leftWeight !== rightWeight) {
        return leftWeight - rightWeight;
      }

      return String(left.title || "").localeCompare(String(right.title || ""));
    });

  const domainFilteredOptions = filterPaymentOptionsByDomain(options, companyDetails);

  return domainFilteredOptions.length > 0
    ? domainFilteredOptions
    : filterPaymentOptionsByDomain(getLegacyFallbackOptions(countryCode), companyDetails);
};

export const getSelectedPaymentOption = (paymentOptions = [], paymentMethod = "") => (
  paymentOptions.find((option) => option.value === paymentMethod) || paymentOptions[0] || null
);

export const isCashPaymentOption = (paymentOptionOrValue) => {
  if (typeof paymentOptionOrValue === "string") {
    return normalizeValue(paymentOptionOrValue) === "cash";
  }

  return paymentOptionOrValue?.category === "offline_cash";
};

export const getOfflinePaymentSavedMessage = (paymentOption) => {
  const paymentTitle = paymentOption?.title || "Payment";
  return isCashPaymentOption(paymentOption)
    ? `${paymentTitle} payment saved`
    : `${paymentTitle} payment saved as pending verification.`;
};

export const isOfflinePaymentOption = (paymentOptionOrValue) => {
  if (!paymentOptionOrValue) return false;
  if (typeof paymentOptionOrValue === "string") return false;

  return Boolean(paymentOptionOrValue.isOffline);
};

export const requiresOfflineReference = (paymentOptionOrValue) => {
  if (!paymentOptionOrValue || typeof paymentOptionOrValue === "string") return false;

  return Boolean(paymentOptionOrValue.requiresReference);
};

export const getManualPaymentDisplayDetails = (companyDetails, paymentOption, orderNumber = "") => {
  const paymentMethod = paymentOption?.paymentMethodConfig || null;
  const paymentTitle = paymentOption?.title || paymentMethod?.name || "Manual Payment";
  const recipientName = paymentMethod?.nameOnAccount
    || companyDetails?.name
    || paymentMethod?.name
    || "Payment Account";
  const recipientTag = paymentMethod?.accountTag
    || paymentMethod?.nameOnAccount
    || paymentMethod?.name
    || companyDetails?.name
    || "Payment Account";

  let referenceLabel = `Use order #${orderNumber || "pending"}`;

  if (paymentMethod?.routingNumber) {
    referenceLabel = `Routing • ${paymentMethod.routingNumber}`;
  } else if (paymentMethod?.accountNumber) {
    referenceLabel = `Acct • ${String(paymentMethod.accountNumber).slice(-4)}`;
  } else if (paymentMethod?.accountTag) {
    referenceLabel = paymentMethod.accountTag;
  } else if (paymentMethod?.email) {
    referenceLabel = paymentMethod.email;
  } else if (paymentMethod?.phoneNumber) {
    referenceLabel = String(paymentMethod.phoneNumber);
  }

  return {
    title: paymentTitle,
    recipientName,
    recipientTag,
    referenceLabel,
    recipientEmail: paymentMethod?.email || "",
    recipientPhone: paymentMethod?.phoneNumber || "",
    recipientCountryCode: paymentMethod?.countryCode || "",
    qrImageUrl: getPaymentMethodQrImageUrl(paymentMethod),
    confirmLabel: `Confirm ${paymentTitle} Payment`,
    dialogTitle: `Pay via ${paymentTitle}`,
    helperText: `Paste the ${paymentTitle} confirmation/reference ID after sending the payment.`
  };
};

export const getZellePaymentMethod = (paymentMethods = []) => (
  getConfiguredPaymentMethods(paymentMethods).find(isZelleMethod)
  || getConfiguredPaymentMethods(paymentMethods).find(isManualReferenceMethod)
  || null
);

export const getZelleRecipientName = (companyDetails, zellePaymentMethod) => (
  getManualPaymentDisplayDetails(companyDetails, { paymentMethodConfig: zellePaymentMethod }).recipientName
);

export const getZelleReferenceLabel = (zellePaymentMethod, orderNumber = "") => (
  getManualPaymentDisplayDetails(null, { paymentMethodConfig: zellePaymentMethod }, orderNumber).referenceLabel
);

export const getOrderPaymentMethodLabel = (paymentMethod, countryCode = "") => {
  const normalizedMethod = String(paymentMethod || "").toUpperCase();

  switch (normalizedMethod) {
    case "PAYMENT_GATEWAY":
      return getGatewayDisplayLabel(countryCode);
    case "CASH":
      return "Cash";
    case "ZELLE":
      return "Zelle";
    case "STRIPE":
      return "Card Payment";
    case "RAZORPAY":
      return "Razorpay";
    case "BANK_TRANSFER":
      return "Bank Transfer";
    case "BANK_ACCOUNT":
      return "Bank Transfer";
    case "UPI":
      return "UPI";
    case "QR_CODE":
      return "QR Code";
    case "PAYMENT_APP":
      return paymentMethod || "Payment App";
    case "CARD_PAYMENT":
      return getGatewayDisplayLabel(countryCode);
    default:
      return paymentMethod || "Card";
  }
};

export const getOfflineRedirectPath = (domainType = "") => (
  String(domainType).toUpperCase() === "EDUCATION" ? "/my-orders" : "/enrollments"
);

export const getOfflineOrderStatus = (paymentMethod = "") => {
  const normalizedPaymentMethod = String(paymentMethod || "").toLowerCase();

  if (normalizedPaymentMethod === "cash") {
    return OFFLINE_PAYMENT_PENDING_STATUS;
  }

  return OFFLINE_PAYMENT_PROCESSING_STATUS;
};

export const isOfflinePaymentReviewStatus = (status = "") => (
  [OFFLINE_PAYMENT_PENDING_STATUS, OFFLINE_PAYMENT_PROCESSING_STATUS]
    .includes(String(status || "").toUpperCase())
);

export const getOfflinePaymentStatusLabel = (status = "") => {
  const normalizedStatus = String(status || "").toUpperCase();

  if (normalizedStatus === OFFLINE_PAYMENT_PROCESSING_STATUS) {
    return "Payment Processing";
  }

  if (normalizedStatus === OFFLINE_PAYMENT_PENDING_STATUS) {
    return "Payment Pending";
  }

  return status;
};

export const normalizeOfflineReferenceId = (referenceId = "") => (
  String(referenceId || "").trim()
);

export const buildZellePendingTransaction = ({
  amount,
  orderPrimaryId,
  accountPrimaryId,
  userPrimaryId,
  company,
  zellePaymentMethod,
  paymentMethodConfig,
  referenceId,
  createdBy
}) => {
  const normalizedReferenceId = normalizeOfflineReferenceId(referenceId);
  const selectedPaymentMethod = paymentMethodConfig || zellePaymentMethod;

  if (
    !normalizedReferenceId
    || !orderPrimaryId
    || !accountPrimaryId
    || !userPrimaryId
    || !company?.primaryId
  ) {
    return null;
  }

  return {
    amount: Number(amount) || 0,
    date: new Date().toISOString(),
    status: "PRE_AUTH",
    type: "PURCHASE",
    paymentMethod: {
      paymentMethodType: selectedPaymentMethod?.paymentMethodType || "BANK_ACCOUNT",
      name: selectedPaymentMethod?.name || "Offline Payment",
      isDefault: Boolean(selectedPaymentMethod?.isDefault),
      ...(selectedPaymentMethod?.nameOnAccount ? { nameOnAccount: selectedPaymentMethod.nameOnAccount } : {}),
      ...(selectedPaymentMethod?.routingNumber ? { routingNumber: selectedPaymentMethod.routingNumber } : {}),
      ...(selectedPaymentMethod?.accountTag ? { accountTag: selectedPaymentMethod.accountTag } : {}),
      ...(selectedPaymentMethod?.email ? { email: selectedPaymentMethod.email } : {}),
      ...(selectedPaymentMethod?.phoneNumber ? { phoneNumber: Number(selectedPaymentMethod.phoneNumber) || selectedPaymentMethod.phoneNumber } : {}),
      ...(selectedPaymentMethod?.accountNumber !== undefined && selectedPaymentMethod?.accountNumber !== null
        ? { accountNumber: Number(selectedPaymentMethod.accountNumber) || selectedPaymentMethod.accountNumber }
        : {}),
      ...(selectedPaymentMethod?.appQRCodeImage ? { appQRCodeImage: selectedPaymentMethod.appQRCodeImage } : {})
    },
    orderPrimaryId: Number(orderPrimaryId),
    userPrimaryId: Number(userPrimaryId),
    accountPrimaryId: Number(accountPrimaryId),
    paymentRefId: normalizedReferenceId,
    company: {
      primaryId: company.primaryId,
      ...(company?.domain?.type ? { domain: { type: company.domain.type } } : {})
    },
    createdBy
  };
};

export const buildOfflinePendingTransaction = buildZellePendingTransaction;
